"use client";

/**
 * Postcode-check block — 6 varianten uit Claude Design handoff
 * (landingspaginas/project/postcode-check/PostcodeCheck.jsx).
 *
 * State-machine: idle → loading → success | unavailable
 *   - idle        : postcode-input zichtbaar
 *   - loading     : spinner + "Beschikbaarheid checken voor {pc}…"
 *   - success     : SuccessBanner + naam/email/tel form → POST /api/brochure-lead
 *   - unavailable : "Nog niet actief in {pc}" + reset-link
 *
 * Verschillen vs prototype:
 *   - Echte PDOK-lookup (via /api/postcode) ipv 800ms timeout
 *   - Submit gaat naar /api/brochure-lead met attributie + niche
 *   - "Unavailable" is voor toekomstig gebruik wanneer service-area beperkt
 *     wordt; default is altijd 'success' op valide NL postcode.
 *
 * 6 variants:
 *   premium-rustig   : witte kaart, premium rustig (1)
 *   cream-split      : cream + NL-kaart silhouet rechts (2)
 *   hero-banner      : donker, postcode prominent (3)
 *   compact-inline   : 1-rij in cream banner (4)
 *   inline-cream-card: cream banner met witte kaart (5)
 *   editorial        : oversized vraag + USPs (6)
 *
 * Backward-compat: 'card' → premium-rustig, 'inline' → compact-inline.
 */

import { useState, useEffect, useRef } from "react";
import { pushDataLayer } from "../lib/gtm";
import { getAttribution } from "../lib/attribution";
import { isValidEmail } from "../lib/email";
import type { DomainConfig } from "../types";
import TurnstileWidget from "./TurnstileWidget";

// ─────────── Color tokens ──────────────────────────────────────────────────

const PC = {
  bg: "#F1EEE7",
  bgDark: "#1A1F26",
  bgWarm: "#EDE3D0",
  card: "#FFFFFF",
  ink: "#141C28",
  muted: "#5A6578",
  border: "#E4DED3",
  // CSS-var voor host-themed kleur (Zwaluw → #D96A2A via layout.tsx,
  // BW → #FF6B1A fallback). Werkt in inline style + moderne SVG attrs.
  cta: "var(--terracotta, #FF6B1A)",
  success: "#0E7A4A",
  successBg: "#E8F2EC",
};

// ─────────── Helpers ────────────────────────────────────────────────────────

const NL_POSTCODE_REGEX = /^\d{4}\s?[a-zA-Z]{2}$/;

function isValidPostcode(s: string): boolean {
  return NL_POSTCODE_REGEX.test(s.trim());
}

function formatPostcode(s: string): string {
  const clean = s.replace(/\s/g, "").toUpperCase();
  if (clean.length > 4) return clean.slice(0, 4) + " " + clean.slice(4, 6);
  return clean;
}

function genEventId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ─────────── Iconen ────────────────────────────────────────────────────────

interface IconProps {
  size?: number;
  color?: string;
  strokeWidth?: number;
}

function PinIcon({ size = 18, color = "currentColor", strokeWidth = 1.8 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
         strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function CheckCircleIcon({ size = 20, color = "currentColor", strokeWidth = 1.8 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
         strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <polyline points="9 12 12 15 17 10" />
    </svg>
  );
}

function ArrowRightPC({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function SpinnerIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.5" strokeLinecap="round" aria-hidden>
      <path d="M21 12a9 9 0 1 1-6.219-8.56"
            style={{ transformOrigin: "center", animation: "pcspin 0.8s linear infinite" }} />
      <style>{`@keyframes pcspin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}

function HouseIcon({ size = 18, color = "currentColor", strokeWidth = 1.8 }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color}
         strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 12L12 4l9 8" />
      <path d="M5 10v10h14V10" />
    </svg>
  );
}

// ─────────── Postcode hook ─────────────────────────────────────────────────

type PostcodeState = "idle" | "loading" | "success" | "unavailable";

interface PostcodeApiResponse {
  found?: boolean;
  woonplaats?: string;
  error?: string;
}

interface UsePostcodeReturn {
  pc: string;
  state: PostcodeState;
  error: string;
  city: string | null;
  onChange: (v: string) => void;
  submit: () => Promise<void>;
  reset: () => void;
}

function usePostcodeCheck(domain: DomainConfig): UsePostcodeReturn {
  const [pc, setPc] = useState("");
  const [state, setState] = useState<PostcodeState>("idle");
  const [error, setError] = useState("");
  const [city, setCity] = useState<string | null>(null);

  const onChange = (v: string) => {
    setPc(formatPostcode(v));
    if (state !== "idle") setState("idle");
    if (error) setError("");
  };

  // Vd-BE skip: Nominatim heeft geen straat-data, dus postcode-lookup geeft
  // nul toegevoegde waarde voor BE. Direct doorgaan naar contact-form met
  // "uw regio" als woonplaats — klant typt zelf in stap 2.
  const isBeHost = (domain.domain ?? "").replace(/^www\./, "").endsWith("veiligdouchen.be");

  const submit = async (): Promise<void> => {
    if (!isValidPostcode(pc)) {
      setError("Vul een geldige postcode in (bijv. 1234 AB)");
      return;
    }
    setState("loading");
    setError("");
    const cleaned = pc.replace(/\s/g, "").toUpperCase();
    if (isBeHost) {
      // BE: skip API, ga direct door
      setCity("uw regio");
      setState("success");
      pushDataLayer({
        event: "postcode_check_pass",
        postcode_4: cleaned.slice(0, 4),
        page_id: domain.brochure_page_id,
      });
      return;
    }
    try {
      const res = await fetch(`/api/postcode?pc=${encodeURIComponent(cleaned)}&hn=1`, {
        headers: { Accept: "application/json" },
      });
      const json = (await res.json().catch(() => ({}))) as PostcodeApiResponse;
      // Service-area check: voor nu komen we door heel NL, dus elk valide
      // postcode = success. Stadsnaam komt van PDOK (of fallback "uw regio").
      const woonplaats = typeof json.woonplaats === "string" ? json.woonplaats : null;
      setCity(woonplaats ?? "uw regio");
      setState("success");
      pushDataLayer({
        event: "postcode_check_pass",
        postcode_4: cleaned.slice(0, 4),
        page_id: domain.brochure_page_id,
      });
    } catch {
      // Bij netwerkfout: ga toch door op success ipv klant blokkeren
      setCity("uw regio");
      setState("success");
    }
  };

  const reset = (): void => {
    setState("idle");
    setError("");
    setCity(null);
  };

  return { pc, state, error, city, onChange, submit, reset };
}

// ─────────── BrochureLeadForm — verschijnt na success ──────────────────────

interface BrochureLeadFormProps {
  pc: string;
  domain: DomainConfig;
  niche: string | undefined;
  compact?: boolean;
  dark?: boolean;
}

function BrochureLeadForm({ pc, domain, niche, compact = false, dark = false }: BrochureLeadFormProps) {
  // Default Dhr. — minder klikken, klant kan switchen naar Mevr. indien nodig
  const [aanhef, setAanhef] = useState<"Dhr." | "Mevr.">("Dhr.");
  const [achternaam, setAchternaam] = useState("");
  const [email, setEmail] = useState("");
  const [tel, setTel] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const captchaTokenRef = useRef<string | null>(null);

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: compact ? "13px 16px" : "15px 18px",
    fontSize: compact ? 14 : 15,
    fontFamily: "inherit",
    color: dark ? "#F1EEE7" : PC.ink,
    background: dark ? "rgba(255,255,255,0.06)" : PC.card,
    border: `1px solid ${dark ? "rgba(255,255,255,0.16)" : PC.border}`,
    borderRadius: 10,
    outline: "none",
    boxSizing: "border-box",
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.05em",
    color: dark ? "rgba(241,238,231,0.6)" : PC.muted,
    textTransform: "uppercase",
    marginBottom: 6,
    display: "block",
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!achternaam.trim()) return setError("Vul uw achternaam in.");
    if (!isValidEmail(email)) return setError("Vul een geldig e-mailadres in.");
    if (!tel.trim()) return setError("Vul uw telefoonnummer in — wij nemen contact op.");

    setBusy(true);
    try {
      const eventId = genEventId();
      const attribution = getAttribution() ?? {};
      const res = await fetch("/api/brochure-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          step: 1,
          // Match Hero brochure-form veld-set: aanhef + achternaam ipv first_name.
          // first_name blijft leeg; lead-api accepteert salutation + last_name.
          first_name: "",
          salutation: aanhef,
          last_name: achternaam.trim(),
          email: email.trim().toLowerCase(),
          phone: tel.replace(/\s/g, ""),
          delivery_method: "digital",
          event_id: eventId,
          niche: niche ?? "",
          source_url: typeof window !== "undefined" ? window.location.href : "",
          honeypot: "",
          postal_code: pc.replace(/\s/g, "").toUpperCase(),
          // Bron-tag — sales kan in /brochureaanvragen filteren op
          // lead_source='postcode-check'. promo_action blijft = actieve
          // promo op pagina (kan apart bestaan, hier niet gezet).
          lead_source: "postcode-check",
          ...(captchaTokenRef.current ? { captcha_token: captchaTokenRef.current } : {}),
          ...attribution,
        }),
      });
      if (!res.ok) {
        const j = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? `HTTP ${res.status}`);
      }
      const leadEvt: import("../lib/gtm").BrochureMicroEvent = {
        event: "lead_from_postcode_check",
        page_id: domain.brochure_page_id,
        event_id: eventId,
      };
      const cv = domain.conversion_values;
      if (cv && typeof cv.postcode_check_value === "number") {
        leadEvt.value = cv.postcode_check_value;
        leadEvt.currency = cv.currency;
      }
      pushDataLayer(leadEvt);
      // Markeer ExitIntentPopup als "al gezien" — anders verschijnt 'ie
      // alsnog met een "Claim mijn cadeau" CTA terwijl klant net heeft
      // gesubmit. Onnodige verwarring.
      try { sessionStorage.setItem("bw_exit_intent_seen_v1", "1"); } catch { /* ignore */ }
      setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (sent) {
    return (
      <div style={{
        background: dark ? "rgba(14,122,74,0.18)" : PC.successBg,
        border: `1px solid ${dark ? "rgba(14,122,74,0.4)" : "rgba(14,122,74,0.18)"}`,
        borderRadius: 12,
        padding: compact ? "16px 18px" : "18px 22px",
        display: "flex",
        alignItems: "flex-start",
        gap: 12,
      }}>
        <div style={{ color: PC.success, flexShrink: 0, marginTop: 1 }}>
          <CheckCircleIcon size={22} />
        </div>
        <div>
          <div style={{
            fontWeight: 600,
            fontSize: compact ? 15 : 16,
            color: dark ? "#F1EEE7" : PC.ink,
            marginBottom: 4,
          }}>
            Aanvraag ontvangen!
          </div>
          <div style={{
            fontSize: compact ? 13 : 14,
            lineHeight: 1.5,
            color: dark ? "rgba(241,238,231,0.7)" : PC.muted,
          }}>
            Wij sturen u zo spoedig mogelijk de brochure en nemen binnen 1 werkdag contact op om
            een afspraak in te plannen.
          </div>
          <div style={{
            fontSize: compact ? 12 : 13,
            lineHeight: 1.45,
            color: dark ? "rgba(241,238,231,0.6)" : PC.muted,
            marginTop: 8,
            fontStyle: "italic",
            opacity: 0.85,
          }}>
            Geen bevestigingsmail ontvangen? Controleer uw spam-/ongewenste mailbox.
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} style={{ display: "grid", gap: 10 }}>
      <div style={{
        display: "grid",
        gridTemplateColumns: compact ? "100px 1fr" : "120px 1fr",
        gap: 10,
      }}>
        <div>
          <label style={labelStyle}>Aanhef</label>
          <select
            required
            value={aanhef}
            onChange={(e) => setAanhef(e.target.value as "Dhr." | "Mevr.")}
            style={{ ...inputStyle, appearance: "auto" }}
            autoComplete="honorific-prefix"
          >
            <option value="Dhr.">Dhr.</option>
            <option value="Mevr.">Mevr.</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Achternaam</label>
          <input
            type="text"
            required
            placeholder="Achternaam"
            value={achternaam}
            onChange={(e) => setAchternaam(e.target.value)}
            style={inputStyle}
            autoComplete="family-name"
          />
        </div>
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: compact ? "1fr" : "1fr 1fr",
        gap: 10,
      }}>
        <div>
          <label style={labelStyle}>E-mailadres</label>
          <input
            type="email"
            required
            placeholder="uw@email.nl"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
            autoComplete="email"
          />
        </div>
        <div>
          <label style={labelStyle}>Telefoonnummer</label>
          <input
            type="tel"
            required
            placeholder="06 12 34 56 78"
            value={tel}
            onChange={(e) => setTel(e.target.value)}
            style={inputStyle}
            autoComplete="tel"
          />
        </div>
      </div>
      {error ? (
        <div style={{ fontSize: 12, color: "#D45151", marginTop: 2 }} role="alert">
          {error}
        </div>
      ) : null}
      {/* Cloudflare Turnstile — invisible bij normaal gedrag, blokkert bots. */}
      <TurnstileWidget tokenRef={captchaTokenRef} />
      <button type="submit" disabled={busy} style={{
        marginTop: 6,
        padding: compact ? "14px 22px" : "16px 24px",
        fontSize: compact ? 14.5 : 15.5,
        fontWeight: 600,
        fontFamily: "inherit",
        background: PC.cta,
        color: "white",
        border: "none",
        borderRadius: 999,
        cursor: busy ? "wait" : "pointer",
        boxShadow: "0 14px 28px -14px rgba(255,107,26,0.6)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        opacity: busy ? 0.7 : 1,
      }}>
        {busy ? <><SpinnerIcon size={15} /> Bezig…</> : <>Stuur mij de brochure <ArrowRightPC /></>}
      </button>
      <div style={{
        fontSize: 11,
        color: dark ? "rgba(241,238,231,0.5)" : PC.muted,
        textAlign: "center",
        marginTop: 4,
      }}>
        Geen verplichting · uw gegevens blijven privé
      </div>
    </form>
  );
}

// ─────────── PostcodeInput — gedeeld door alle varianten ───────────────────

interface PostcodeInputProps {
  pc: string;
  state: PostcodeState;
  error: string;
  onChange: (v: string) => void;
  submit: () => void | Promise<void>;
  dark?: boolean;
  compact?: boolean;
  big?: boolean;
}

function PostcodeInput({
  pc,
  state,
  error,
  onChange,
  submit,
  dark = false,
  compact = false,
  big = false,
}: PostcodeInputProps) {
  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      void submit();
    }
  };
  return (
    <div>
      <div style={{
        display: "flex",
        flexDirection: compact ? "column" : "row",
        gap: compact ? 8 : 10,
        background: dark ? "rgba(255,255,255,0.05)" : PC.card,
        border: `1px solid ${error ? "#D45151" : dark ? "rgba(255,255,255,0.16)" : PC.border}`,
        borderRadius: compact ? 12 : big ? 14 : 12,
        padding: 6,
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: compact ? "10px 12px" : big ? "8px 18px" : "6px 16px",
          flex: 1,
        }}>
          <PinIcon size={big ? 22 : 18} color={dark ? "rgba(241,238,231,0.5)" : PC.muted} />
          <input
            type="text"
            inputMode="text"
            placeholder="1234 AB"
            value={pc}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKey}
            maxLength={7}
            aria-label="Postcode"
            style={{
              flex: 1,
              fontSize: big ? 22 : compact ? 16 : 17,
              fontWeight: big ? 600 : 500,
              fontFamily: "inherit",
              letterSpacing: big ? "0.04em" : "0.02em",
              color: dark ? "#F1EEE7" : PC.ink,
              background: "transparent",
              border: "none",
              outline: "none",
              padding: big ? "10px 0" : compact ? "0" : "10px 0",
              textTransform: "uppercase",
              minWidth: 0,
            }}
          />
        </div>
        <button onClick={() => void submit()} disabled={state === "loading"} style={{
          padding: compact ? "13px 20px" : big ? "14px 22px" : "14px 22px",
          fontSize: compact ? 14 : big ? 14.5 : 15,
          fontWeight: 600,
          fontFamily: "inherit",
          background: PC.cta,
          color: "white",
          border: "none",
          borderRadius: compact ? 8 : big ? 10 : 8,
          cursor: state === "loading" ? "wait" : "pointer",
          opacity: state === "loading" ? 0.85 : 1,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          flexShrink: 0,
          boxShadow: "0 8px 18px -10px rgba(255,107,26,0.55)",
        }}>
          {state === "loading" ? (
            <><SpinnerIcon size={15} /> Bezig…</>
          ) : (
            <>Check {!compact && "beschikbaarheid"} <ArrowRightPC /></>
          )}
        </button>
      </div>
      {error ? (
        <div style={{ fontSize: 12, color: "#D45151", marginTop: 8, paddingLeft: 4 }} role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

// ─────────── Result-banners ────────────────────────────────────────────────

interface BannerProps {
  pc: string;
  city?: string | null;
  compact?: boolean;
  dark?: boolean;
}

function SuccessBanner({ pc, city, compact = false, dark = false }: BannerProps) {
  const label = city || pc;
  return (
    <div style={{
      background: dark ? "rgba(14,122,74,0.18)" : PC.successBg,
      border: `1px solid ${dark ? "rgba(14,122,74,0.4)" : "rgba(14,122,74,0.22)"}`,
      borderRadius: 12,
      padding: compact ? "14px 16px" : "16px 20px",
      display: "flex",
      alignItems: "flex-start",
      gap: 12,
    }}>
      <div style={{ color: PC.success, flexShrink: 0, marginTop: 1 }}>
        <CheckCircleIcon size={22} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{
          fontSize: compact ? 14 : 15,
          lineHeight: 1.5,
          color: dark ? "#F1EEE7" : PC.ink,
        }}>
          <strong>Goed nieuws</strong> — wij komen ook in <strong>{label}</strong>. Laat hieronder
          uw gegevens achter, dan sturen wij u direct de gratis brochure.
        </div>
      </div>
    </div>
  );
}

function UnavailableBanner({
  pc,
  compact = false,
  dark = false,
  onReset,
}: BannerProps & { onReset: () => void }) {
  return (
    <div style={{
      background: dark ? "rgba(255,107,26,0.1)" : "#FBEEE2",
      border: `1px solid ${dark ? "rgba(255,107,26,0.3)" : "rgba(255,107,26,0.25)"}`,
      borderRadius: 12,
      padding: compact ? "14px 16px" : "16px 20px",
    }}>
      <div style={{
        fontSize: compact ? 14 : 15,
        lineHeight: 1.5,
        color: dark ? "#F1EEE7" : PC.ink,
        marginBottom: 6,
      }}>
        <strong>Nog niet actief in {pc}</strong> — laat uw nummer achter, dan bellen wij u terug.
      </div>
      <button onClick={onReset} type="button" style={{
        background: "transparent",
        border: "none",
        padding: 0,
        color: PC.cta,
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "inherit",
        textDecoration: "underline",
      }}>
        Andere postcode proberen
      </button>
    </div>
  );
}

// ─────────── NL-kaart silhouet (variants 2 + 6) ────────────────────────────

interface NLMapProps {
  width?: number;
  accentRegion?: { x: number; y: number } | null;
}

function NLMap({ width = 280, accentRegion = null }: NLMapProps) {
  return (
    <svg width={width} height={width * 1.1} viewBox="0 0 280 308" fill="none" aria-hidden>
      <defs>
        <pattern id="nlgrid" width="14" height="14" patternUnits="userSpaceOnUse">
          <path d="M 14 0 L 0 0 0 14" fill="none" stroke="rgba(20,28,40,0.06)" strokeWidth="0.5" />
        </pattern>
      </defs>
      <path
        d="M 90 30 L 130 22 L 165 28 L 195 38 L 220 60 L 240 95 L 245 130 L 235 160 L 245 185 L 240 215 L 220 245 L 200 270 L 175 285 L 140 290 L 105 285 L 80 270 L 65 245 L 60 215 L 75 195 L 65 170 L 55 145 L 45 120 L 50 95 L 65 65 L 90 30 Z"
        fill="#F5EFE3"
        stroke={PC.cta}
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M 90 30 L 130 22 L 165 28 L 195 38 L 220 60 L 240 95 L 245 130 L 235 160 L 245 185 L 240 215 L 220 245 L 200 270 L 175 285 L 140 290 L 105 285 L 80 270 L 65 245 L 60 215 L 75 195 L 65 170 L 55 145 L 45 120 L 50 95 L 65 65 L 90 30 Z"
        fill="url(#nlgrid)"
      />
      {[
        { x: 100, y: 130 }, { x: 90, y: 155 }, { x: 140, y: 195 },
        { x: 165, y: 145 }, { x: 195, y: 245 }, { x: 215, y: 175 },
        { x: 175, y: 80 },
      ].map((c, i) => (
        <g key={i}>
          <circle cx={c.x} cy={c.y} r={4} fill={PC.cta} />
          <circle cx={c.x} cy={c.y} r={10} fill={PC.cta} opacity={0.18} />
        </g>
      ))}
      {accentRegion ? (
        <g transform={`translate(${accentRegion.x - 12}, ${accentRegion.y - 28})`}>
          <path
            d="M 12 0 C 5 0 0 5 0 12 C 0 22 12 30 12 30 C 12 30 24 22 24 12 C 24 5 19 0 12 0 Z"
            fill={PC.cta}
            stroke="#fff"
            strokeWidth="2"
          />
          <circle cx="12" cy="12" r="4" fill="#fff" />
        </g>
      ) : null}
    </svg>
  );
}

// ─────────── Helper: useCompactMq → CSS-only via class ──────────────────────
//
// In de prototypes wordt `compact` als prop doorgegeven. Voor SSR + per-page
// theming is een puur CSS-only oplossing schoner: we renderen altijd de
// desktop-versie en laten media-queries op `[data-compact]` of body-class
// de mobile-overrides regelen. Sommige variants hebben echter te veel
// state-conditional rendering voor pure CSS — daar zetten we een kleine
// `useIsCompact()` hook in via window.matchMedia.

function useIsCompact(): boolean {
  // SSR-safe: initial false, na hydration update via matchMedia. Eerdere
  // versie gebruikte een globale singleton-flag waardoor alleen de éérste
  // form-instance de listener kreeg en setCompact ook nog buiten useEffect
  // werd aangeroepen → mobile-layout werkte niet.
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setCompact(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener("change", update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", update);
      else mq.removeListener(update);
    };
  }, []);
  return compact;
}

// ============================================================
// VARIANT 1 — premium-rustig (witte kaart)
// ============================================================

function VariantPremiumRustig({ domain, niche }: { domain: DomainConfig; niche?: string }) {
  const { pc, state, error, city, onChange, submit, reset } = usePostcodeCheck(domain);
  const compact = useIsCompact();
  return (
    <section style={{
      width: "100%",
      background: PC.bg,
      padding: compact ? "32px 16px" : "56px 56px",
      fontFamily: "system-ui, sans-serif",
    }}>
      <div style={{
        maxWidth: 720,
        margin: "0 auto",
        background: PC.card,
        border: `1px solid ${PC.border}`,
        borderRadius: 18,
        padding: compact ? "26px 22px" : "40px 44px",
        boxShadow: "0 20px 44px -22px rgba(20,28,40,0.16), 0 4px 12px -8px rgba(20,28,40,0.08)",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: `${PC.cta}18`, color: PC.cta,
            display: "grid", placeItems: "center",
          }}>
            <PinIcon size={16} />
          </div>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.22em",
            color: PC.muted, textTransform: "uppercase",
          }}>
            Postcode-check
          </div>
        </div>
        <h3 style={{
          fontFamily: "'Fraunces', serif",
          fontSize: compact ? 28 : 36,
          fontWeight: 500,
          color: PC.ink,
          lineHeight: 1.1,
          letterSpacing: "-0.02em",
          margin: "0 0 10px",
        }}>
          Werken jullie ook in <span style={{ fontStyle: "italic" }}>mijn regio?</span>
        </h3>
        <p style={{
          fontSize: compact ? 14 : 15,
          color: PC.muted,
          lineHeight: 1.55,
          margin: "0 0 22px",
        }}>
          Vul uw postcode in — wij bezoeken klanten door heel Nederland en kunnen u meestal binnen
          2 weken een advies-afspraak aanbieden.
        </p>
        <PostcodeInput pc={pc} state={state} error={error} onChange={onChange} submit={submit} compact={compact} />
        {state === "success" ? (
          <div style={{ marginTop: 18, display: "grid", gap: 18 }}>
            <SuccessBanner pc={pc} city={city} compact={compact} />
            <BrochureLeadForm pc={pc} domain={domain} niche={niche} compact={compact} />
          </div>
        ) : null}
        {state === "unavailable" ? (
          <div style={{ marginTop: 18 }}>
            <UnavailableBanner pc={pc} compact={compact} onReset={reset} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ============================================================
// VARIANT 2 — cream-split (NL-kaart silhouet rechts)
// ============================================================

function VariantCreamSplit({ domain, niche }: { domain: DomainConfig; niche?: string }) {
  const { pc, state, error, city, onChange, submit, reset } = usePostcodeCheck(domain);
  const compact = useIsCompact();
  const pin =
    state === "success"
      ? {
          x: 130 + ((parseInt(pc.slice(0, 2), 10) || 50) % 80),
          y: 100 + ((parseInt(pc.slice(2, 4), 10) || 50) % 130),
        }
      : null;
  return (
    <section style={{
      width: "100%",
      background: PC.bgWarm,
      padding: compact ? "32px 16px" : "56px 56px",
      fontFamily: "system-ui, sans-serif",
      borderTop: `1px solid ${PC.border}`,
      borderBottom: `1px solid ${PC.border}`,
    }}>
      <div style={{
        maxWidth: 1100,
        margin: "0 auto",
        background: PC.card,
        border: `1px solid ${PC.border}`,
        borderRadius: 20,
        overflow: "hidden",
        boxShadow: "0 24px 48px -28px rgba(20,28,40,0.18)",
        display: "grid",
        gridTemplateColumns: compact ? "1fr" : "1.2fr 1fr",
      }}>
        <div style={{ padding: compact ? "26px 22px" : "44px 48px" }}>
          <div style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.22em",
            color: PC.cta, textTransform: "uppercase", marginBottom: 14,
          }}>
            Postcode-check
          </div>
          <h3 style={{
            fontFamily: "'Fraunces', serif",
            fontSize: compact ? 28 : 38,
            fontWeight: 500,
            color: PC.ink,
            lineHeight: 1.05,
            letterSpacing: "-0.02em",
            margin: "0 0 10px",
          }}>
            Wij komen door
            <br />
            <span style={{ fontStyle: "italic" }}>heel</span> Nederland.
          </h3>
          <p style={{
            fontSize: compact ? 14 : 15,
            color: PC.muted,
            lineHeight: 1.55,
            margin: "0 0 22px",
            maxWidth: 380,
          }}>
            Vul uw postcode in en zie meteen of we bij u langs kunnen komen — meestal binnen 2 weken.
          </p>
          <PostcodeInput pc={pc} state={state} error={error} onChange={onChange} submit={submit} compact={compact} />
          {state === "success" ? (
            <div style={{ marginTop: 16, display: "grid", gap: 14 }}>
              <SuccessBanner pc={pc} city={city} compact={compact} />
              <BrochureLeadForm pc={pc} domain={domain} niche={niche} compact={compact} />
            </div>
          ) : null}
          {state === "unavailable" ? (
            <div style={{ marginTop: 16 }}>
              <UnavailableBanner pc={pc} compact={compact} onReset={reset} />
            </div>
          ) : null}
        </div>
        {!compact ? (
          <div style={{
            background: `linear-gradient(160deg, ${PC.bgWarm} 0%, #DFD3BA 100%)`,
            display: "grid",
            placeItems: "center",
            padding: 32,
            position: "relative",
            overflow: "hidden",
            borderLeft: `1px solid ${PC.border}`,
          }}>
            <div style={{
              position: "absolute", inset: 0,
              backgroundImage:
                "linear-gradient(rgba(20,28,40,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(20,28,40,0.04) 1px, transparent 1px)",
              backgroundSize: "20px 20px",
            }} />
            <NLMap width={260} accentRegion={pin} />
            {state === "success" ? (
              <div style={{
                position: "absolute", bottom: 24,
                background: PC.card, padding: "10px 14px", borderRadius: 999,
                fontSize: 12, fontWeight: 600, color: PC.success,
                boxShadow: "0 8px 20px -8px rgba(20,28,40,0.2)",
                display: "flex", alignItems: "center", gap: 6,
              }}>
                <CheckCircleIcon size={14} /> {pc} gevonden
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ============================================================
// VARIANT 3 — hero-banner (donker)
// ============================================================

function VariantHeroBanner({ domain, niche }: { domain: DomainConfig; niche?: string }) {
  const { pc, state, error, city, onChange, submit, reset } = usePostcodeCheck(domain);
  const compact = useIsCompact();
  return (
    <section style={{
      width: "100%",
      background: PC.bgDark,
      color: "#F1EEE7",
      padding: compact ? "40px 20px" : "64px 56px",
      fontFamily: "system-ui, sans-serif",
      position: "relative",
      overflow: "hidden",
    }}>
      <div aria-hidden style={{
        position: "absolute", left: "-10%", top: "30%",
        width: 600, height: 600,
        background: "radial-gradient(circle, rgba(255,107,26,0.12) 0%, transparent 60%)",
        pointerEvents: "none",
      }} />
      <div aria-hidden style={{
        position: "absolute", inset: 0, pointerEvents: "none", opacity: 0.04,
        backgroundImage: "repeating-linear-gradient(115deg, #F1EEE7 0 1px, transparent 1px 40px)",
      }} />
      <div style={{
        position: "relative",
        maxWidth: 720,
        margin: "0 auto",
        textAlign: "center",
      }}>
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 8,
          padding: "6px 14px", borderRadius: 999,
          background: "rgba(255,107,26,0.14)",
          color: PC.cta,
          fontSize: 11, fontWeight: 700, letterSpacing: "0.22em",
          textTransform: "uppercase", marginBottom: 18,
        }}>
          <PinIcon size={12} strokeWidth={2.2} /> Postcode-check
        </div>
        <h3 style={{
          fontFamily: "'Fraunces', serif",
          fontSize: compact ? 32 : 50,
          fontWeight: 500,
          color: "#F1EEE7",
          lineHeight: 1.0,
          letterSpacing: "-0.02em",
          margin: "0 0 14px",
        }}>
          Komen wij ook bij <span style={{ fontStyle: "italic", color: PC.cta }}>u</span> langs?
        </h3>
        <p style={{
          fontSize: compact ? 14.5 : 16,
          lineHeight: 1.6,
          color: "rgba(241,238,231,0.72)",
          margin: "0 auto 28px",
          maxWidth: 480,
        }}>
          Vul uw postcode in — wij plannen meestal binnen 2 weken een persoonlijke advies-afspraak
          bij u thuis.
        </p>
        <div style={{ maxWidth: 520, margin: "0 auto" }}>
          <PostcodeInput
            pc={pc}
            state={state}
            error={error}
            onChange={onChange}
            submit={submit}
            dark
            compact={compact}
            big={!compact}
          />
          {state === "success" ? (
            <div style={{ marginTop: 18, display: "grid", gap: 14, textAlign: "left" }}>
              <SuccessBanner pc={pc} city={city} compact={compact} dark />
              <div style={{
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 14,
                padding: compact ? "20px 18px" : "24px 26px",
              }}>
                <BrochureLeadForm pc={pc} domain={domain} niche={niche} compact={compact} dark />
              </div>
            </div>
          ) : null}
          {state === "unavailable" ? (
            <div style={{ marginTop: 18, textAlign: "left" }}>
              <UnavailableBanner pc={pc} compact={compact} dark onReset={reset} />
            </div>
          ) : null}
        </div>
        {state === "idle" ? (
          <div style={{
            display: "flex", justifyContent: "center", gap: 28,
            marginTop: 28, flexWrap: "wrap",
            fontSize: 12, color: "rgba(241,238,231,0.55)",
          }}>
            {[
              { i: <CheckCircleIcon size={14} />, t: "Heel Nederland" },
              { i: <HouseIcon size={14} />, t: "Bij u thuis" },
              { i: <PinIcon size={14} />, t: "Binnen 2 weken" },
            ].map((b, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: PC.cta }}>{b.i}</span> {b.t}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ============================================================
// VARIANT 4 — compact-inline (1-rij banner)
// ============================================================

function VariantCompactInline({ domain, niche }: { domain: DomainConfig; niche?: string }) {
  const { pc, state, error, city, onChange, submit, reset } = usePostcodeCheck(domain);
  const compact = useIsCompact();
  return (
    <section style={{
      width: "100%",
      background: PC.bgWarm,
      padding: compact ? "32px 16px" : "56px 56px",
      fontFamily: "system-ui, sans-serif",
      borderTop: `1px solid ${PC.border}`,
      borderBottom: `1px solid ${PC.border}`,
    }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        {state === "idle" ? (
          <div style={{
            display: "grid",
            gridTemplateColumns: compact ? "1fr" : "auto 1fr auto",
            gap: compact ? 20 : 32,
            alignItems: "center",
          }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 12,
              justifyContent: compact ? "center" : "flex-start",
            }}>
              <div style={{
                width: 56, height: 56, borderRadius: 14,
                background: PC.ink, color: PC.bgWarm,
                display: "grid", placeItems: "center", flexShrink: 0,
              }}>
                <PinIcon size={24} />
              </div>
            </div>
            <div style={{ textAlign: compact ? "center" : "left" }}>
              <div style={{
                fontFamily: "'Fraunces', serif",
                fontSize: compact ? 24 : 28,
                fontWeight: 500,
                color: PC.ink,
                lineHeight: 1.15,
                letterSpacing: "-0.015em",
              }}>
                Werken wij in <span style={{ fontStyle: "italic" }}>uw</span> regio?
              </div>
              <div style={{
                fontSize: compact ? 13.5 : 14.5,
                color: PC.muted,
                lineHeight: 1.45,
                marginTop: 4,
              }}>
                Check uw postcode — antwoord direct.
              </div>
            </div>
            <div style={{ minWidth: compact ? "auto" : 380 }}>
              <PostcodeInput pc={pc} state={state} error={error} onChange={onChange} submit={submit} compact={compact} />
            </div>
          </div>
        ) : null}
        {state === "loading" ? (
          <div style={{
            fontSize: 14, color: PC.muted, textAlign: "center",
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            width: "100%", padding: "20px 0",
          }}>
            <SpinnerIcon size={14} /> Beschikbaarheid checken voor {pc}…
          </div>
        ) : null}
        {state === "success" ? (
          <div style={{ display: "grid", gap: 14 }}>
            <SuccessBanner pc={pc} city={city} compact={compact} />
            <div style={{
              background: PC.card, border: `1px solid ${PC.border}`,
              borderRadius: 14,
              padding: compact ? "20px 18px" : "22px 24px",
            }}>
              <BrochureLeadForm pc={pc} domain={domain} niche={niche} compact={compact} />
            </div>
          </div>
        ) : null}
        {state === "unavailable" ? (
          <UnavailableBanner pc={pc} compact={compact} onReset={reset} />
        ) : null}
      </div>
    </section>
  );
}

// ============================================================
// VARIANT 5 — inline-cream-card
// ============================================================

function VariantInlineCreamCard({ domain, niche }: { domain: DomainConfig; niche?: string }) {
  const { pc, state, error, city, onChange, submit, reset } = usePostcodeCheck(domain);
  const compact = useIsCompact();
  return (
    <section style={{
      width: "100%",
      background: PC.bgWarm,
      padding: compact ? "32px 16px" : "56px 56px",
      fontFamily: "system-ui, sans-serif",
      borderTop: `1px solid ${PC.border}`,
      borderBottom: `1px solid ${PC.border}`,
    }}>
      <div style={{
        maxWidth: 1100,
        margin: "0 auto",
        background: PC.card,
        border: `1px solid ${PC.border}`,
        borderRadius: 16,
        padding: compact ? "24px 22px" : "28px 36px",
        boxShadow: "0 16px 32px -22px rgba(20,28,40,0.14)",
      }}>
        {state === "idle" ? (
          <div style={{
            display: "grid",
            gridTemplateColumns: compact ? "1fr" : "1fr auto",
            gap: compact ? 20 : 32,
            alignItems: "center",
          }}>
            <div style={{ textAlign: compact ? "center" : "left" }}>
              <div style={{
                display: "inline-flex", alignItems: "center", gap: 8,
                fontSize: 11, fontWeight: 700, letterSpacing: "0.22em",
                color: PC.cta, textTransform: "uppercase", marginBottom: 6,
              }}>
                <PinIcon size={12} strokeWidth={2.2} /> Postcode-check
              </div>
              <div style={{
                fontFamily: "'Fraunces', serif",
                fontSize: compact ? 24 : 28,
                fontWeight: 500,
                color: PC.ink,
                lineHeight: 1.15,
                letterSpacing: "-0.015em",
                margin: "0 0 4px",
              }}>
                Werken jullie ook in <span style={{ fontStyle: "italic" }}>mijn regio?</span>
              </div>
              <div style={{
                fontSize: compact ? 13.5 : 14.5,
                color: PC.muted,
                lineHeight: 1.5,
                maxWidth: 420,
              }}>
                Wij komen door heel Nederland — check uw postcode voor een afspraak binnen 2 weken.
              </div>
            </div>
            <div style={{ minWidth: compact ? "auto" : 380 }}>
              <PostcodeInput pc={pc} state={state} error={error} onChange={onChange} submit={submit} compact={compact} />
            </div>
          </div>
        ) : null}
        {state === "loading" ? (
          <div style={{
            fontSize: 14, color: PC.muted, textAlign: "center",
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            width: "100%", padding: "20px 0",
          }}>
            <SpinnerIcon size={14} /> Beschikbaarheid checken voor {pc}…
          </div>
        ) : null}
        {state === "success" ? (
          <div style={{ display: "grid", gap: 14 }}>
            <SuccessBanner pc={pc} city={city} compact={compact} />
            <BrochureLeadForm pc={pc} domain={domain} niche={niche} compact={compact} />
          </div>
        ) : null}
        {state === "unavailable" ? (
          <UnavailableBanner pc={pc} compact={compact} onReset={reset} />
        ) : null}
      </div>
    </section>
  );
}

// ============================================================
// VARIANT 6 — editorial (oversized vraag + USPs)
// ============================================================

function VariantEditorial({ domain, niche }: { domain: DomainConfig; niche?: string }) {
  const { pc, state, error, city, onChange, submit, reset } = usePostcodeCheck(domain);
  const compact = useIsCompact();
  return (
    <section style={{
      width: "100%",
      background: PC.bgWarm,
      padding: compact ? "36px 16px" : "64px 56px",
      fontFamily: "system-ui, sans-serif",
      borderTop: `1px solid ${PC.border}`,
      borderBottom: `1px solid ${PC.border}`,
      position: "relative",
      overflow: "hidden",
    }}>
      <div aria-hidden style={{
        position: "absolute", right: -100, top: -40,
        opacity: 0.08, pointerEvents: "none",
      }}>
        <NLMap width={420} />
      </div>
      <div style={{
        position: "relative",
        maxWidth: 1100,
        margin: "0 auto",
      }}>
        {state === "idle" ? (
          <div style={{
            display: "grid",
            gridTemplateColumns: compact ? "1fr" : "1.3fr 1fr",
            gap: compact ? 24 : 48,
            alignItems: "center",
          }}>
            <div style={{ textAlign: compact ? "center" : "left" }}>
              <div style={{
                fontSize: 11, fontWeight: 700, letterSpacing: "0.22em",
                color: PC.cta, textTransform: "uppercase", marginBottom: 12,
              }}>
                Postcode-check
              </div>
              <h3 style={{
                fontFamily: "'Fraunces', serif",
                fontSize: compact ? 30 : 44,
                fontWeight: 500,
                color: PC.ink,
                lineHeight: 1.0,
                letterSpacing: "-0.025em",
                margin: "0 0 6px",
              }}>
                Werken jullie ook
                <br />
                in <span style={{ fontStyle: "italic", color: PC.cta }}>mijn</span> regio?
              </h3>
              <div style={{
                display: "flex", gap: 18,
                justifyContent: compact ? "center" : "flex-start",
                marginTop: 16,
                fontSize: 13, color: PC.muted, flexWrap: "wrap",
              }}>
                {[
                  { i: <CheckCircleIcon size={14} />, t: "Heel Nederland" },
                  { i: <HouseIcon size={14} />, t: "Bij u thuis" },
                  { i: <PinIcon size={14} />, t: "Binnen 2 weken" },
                ].map((b, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: PC.cta }}>{b.i}</span> {b.t}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <PostcodeInput
                pc={pc}
                state={state}
                error={error}
                onChange={onChange}
                submit={submit}
                compact={compact}
                big={!compact}
              />
              <div style={{
                fontSize: 12, color: PC.muted, marginTop: 10,
                textAlign: compact ? "center" : "left",
              }}>
                Bijvoorbeeld{" "}
                <span style={{ color: PC.ink, fontFamily: "monospace", fontWeight: 600 }}>
                  1234 AB
                </span>
                {" "}— antwoord direct.
              </div>
            </div>
          </div>
        ) : null}
        {state === "loading" ? (
          <div style={{
            fontSize: 14, color: PC.muted, textAlign: "center",
            display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
            width: "100%", padding: "20px 0",
          }}>
            <SpinnerIcon size={14} /> Beschikbaarheid checken voor {pc}…
          </div>
        ) : null}
        {state === "success" ? (
          <div style={{
            display: "grid", gap: 14,
            maxWidth: 720, margin: "0 auto",
            background: PC.card,
            border: `1px solid ${PC.border}`,
            borderRadius: 14,
            padding: compact ? "22px 20px" : "28px 32px",
          }}>
            <SuccessBanner pc={pc} city={city} compact={compact} />
            <BrochureLeadForm pc={pc} domain={domain} niche={niche} compact={compact} />
          </div>
        ) : null}
        {state === "unavailable" ? (
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <UnavailableBanner pc={pc} compact={compact} onReset={reset} />
          </div>
        ) : null}
      </div>
    </section>
  );
}

// ─────────── Public block-component ────────────────────────────────────────

export interface PostcodeCheckContent {
  /** Backward-compat alleen — nieuwe varianten gebruiken zelf hun copy. */
  headline?: string;
  subhead?: string;
  placeholder?: string;
  cta_check?: string;
  confirm_text?: string;
  cta_submit?: string;
  success_text?: string;
}

export type PostcodeCheckVariant =
  | "premium-rustig"
  | "cream-split"
  | "hero-banner"
  | "compact-inline"
  | "inline-cream-card"
  | "editorial"
  // Legacy aliases (gemapt op nieuwe variants)
  | "card"
  | "inline";

interface Props {
  content: PostcodeCheckContent;
  variant?: PostcodeCheckVariant;
  domain: DomainConfig;
  niche?: string;
}

export default function PostcodeCheckBlock({
  variant = "premium-rustig",
  domain,
  niche,
}: Props) {
  // Legacy aliases
  const v: PostcodeCheckVariant =
    variant === "card" ? "premium-rustig" : variant === "inline" ? "compact-inline" : variant;

  const passthrough = { domain, ...(niche ? { niche } : {}) };
  switch (v) {
    case "cream-split":
      return <VariantCreamSplit {...passthrough} />;
    case "hero-banner":
      return <VariantHeroBanner {...passthrough} />;
    case "compact-inline":
      return <VariantCompactInline {...passthrough} />;
    case "inline-cream-card":
      return <VariantInlineCreamCard {...passthrough} />;
    case "editorial":
      return <VariantEditorial {...passthrough} />;
    case "premium-rustig":
    default:
      return <VariantPremiumRustig {...passthrough} />;
  }
}
